import { supabase } from '@/lib/supabase';
import { Conversation, Message, Model } from '@shared/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { OutputFormat } from '@/lib/outputFormatCache';
import {
  initializeConversationLeaf,
  messageInsertedConversationUpdate,
  upsertStreamedMessage,
} from './cacheUpdates.ts';
import { useInsertMessageMutation } from './crud.ts';
import { explainComputeError } from './outputFormat.ts';

// Throw a rich Error for a non-ok streaming-chat edge-function response.
// Extracted verbatim (#128) from the byte-identical `!response.ok` blocks in
// useMeshGenChatMutation and useParametricChatMutation. Pulls the body so
// upstream toasts/logs get the real reason (rate limit, billing, edge function
// exception) rather than a generic status+text. Best-effort: body may already
// be consumed or non-textual.
async function throwEdgeFunctionError(
  response: Response,
  functionName: string,
): Promise<never> {
  const bodyText = await response.text().catch(() => '');
  const detail = bodyText ? `\n${bodyText.slice(0, 2000)}` : '';
  // Route through explainComputeError so a known compute failure (e.g. the
  // unitcell/microservice 504 IDLE_TIMEOUT) names its probable cause + the
  // postmortem doc instead of surfacing as a bare gateway code. Unknown
  // failures pass through unchanged.
  throw new Error(
    explainComputeError(
      `Edge function ${response.status} ${response.statusText} (${functionName})${detail}`,
    ),
  );
}

export function useMeshGenChatMutation({
  conversationId,
}: {
  conversationId: string;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();

  return useMutation({
    mutationKey: ['mesh-gen-chat', conversationId],
    mutationFn: async ({
      model,
      messageId,
      conversationId,
    }: {
      model: Model;
      messageId: string;
      conversationId: string;
    }) => {
      const newMessageId = crypto.randomUUID();
      let initialized = false;

      const functionName = 'mesh-gen-chat';
      // Start streaming request
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${
              (await supabase.auth.getSession()).data.session?.access_token
            }`,
          },
          body: JSON.stringify({
            conversationId,
            messageId,
            model,
            newMessageId,
          }),
        },
      );

      if (!response.ok) {
        await throwEdgeFunctionError(response, functionName);
      }

      if (response.headers.get('Content-Type')?.includes('application/json')) {
        const data = await response.json();
        if (data.message) {
          return data.message;
        } else {
          throw new Error('No message received');
        }
      }

      async function initialize() {
        // Cancel any pending queries and update conversation leaf ID
        await initializeConversationLeaf(
          queryClient,
          conversationId,
          newMessageId,
        );
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      const decoder = new TextDecoder();
      let leftover = '';

      let finalMessage: Message | null = null;

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          leftover += decoder.decode(value, { stream: true });

          const lines = leftover.split('\n');
          leftover = lines.pop() ?? '';

          for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line) continue;
            try {
              const data: Message = JSON.parse(line);

              finalMessage = data;

              upsertStreamedMessage(queryClient, conversationId, data);

              if (!initialized) {
                await initialize();
                initialized = true;
              }
            } catch (parseError) {
              console.error('Error parsing streaming data:', parseError);
            }
          }
        }

        const flushRemainder = decoder.decode();
        if (flushRemainder) leftover += flushRemainder;
        const tail = leftover.trim();
        if (tail) {
          try {
            const data: Message = JSON.parse(tail);
            finalMessage = data;
            upsertStreamedMessage(queryClient, conversationId, data);
          } catch (parseError) {
            console.error('Error parsing final streaming data:', parseError);
          }
        }
      } finally {
        reader.releaseLock();
      }

      if (!finalMessage) {
        throw new Error('No final message received');
      }

      return finalMessage;
    },
    onSuccess: (newMessage) => {
      messageInsertedConversationUpdate(
        queryClient,
        newMessage,
        conversationId,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['userExtraData'] });
    },
    onError: async (error, { messageId }) => {
      Sentry.captureException(error, {
        extra: {
          hook: 'useMeshGenChatMutation',
          messageId,
          conversationId,
        },
      });
      // Insert a synthetic assistant message carrying the structured
      // errorLog payload so the ErrorLogBubble fires with a Copy button —
      // without this the user just saw a generic "An error occurred" line
      // with no diagnostic to grab. The bubble's trigger condition includes
      // `content.errorLog` presence, so this is enough to surface it.
      try {
        const errMsg = error instanceof Error ? error.message : String(error);
        const errStack = error instanceof Error ? error.stack : undefined;
        await insertMessageAsync({
          role: 'assistant',
          content: {
            text: 'An error occurred while processing your request.',
            errorLog: {
              kind: 'runtime' as const,
              source: 'mesh-gen-chat',
              message: errMsg,
              detail: errStack,
            },
          },
          parent_message_id: messageId,
          conversation_id: conversationId,
        });
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            hook: 'useMeshGenChatMutation insertMessageAsync',
            messageId,
            conversationId,
          },
        });
      }
    },
  });
}

export function useParametricChatMutation({
  conversationId,
}: {
  conversationId: string;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();

  return useMutation({
    mutationKey: ['parametric-chat', conversationId],
    mutationFn: async ({
      model,
      messageId,
      conversationId,
      outputFormat,
    }: {
      model: Model;
      messageId: string;
      conversationId: string;
      // 'mesh' → mesh-chat (OpenSCAD kernel); 'solidbody' → parametric-chat
      // (build123d kernel). Optional only for backwards compatibility with
      // callers that haven't been migrated; defaults to build123d.
      outputFormat?: OutputFormat;
    }) => {
      const newMessageId = crypto.randomUUID();
      let initialized = false;

      // 'unitcell' → unitcell-chat (NASA Unitcell Engine sidecar). Strictly
      // short-circuit; no LLM upstream — see supabase/functions/unitcell-chat
      // for the implementation. The fall-through default stays
      // parametric-chat so any new OutputFormat enum value added without a
      // matching branch here still lands on build123d rather than 500.
      const functionName:
        | 'mesh-chat'
        | 'parametric-chat'
        | 'implicit-chat'
        | 'unitcell-chat' =
        outputFormat === 'mesh'
          ? 'mesh-chat'
          : outputFormat === 'implicit'
            ? 'implicit-chat'
            : outputFormat === 'unitcell'
              ? 'unitcell-chat'
              : 'parametric-chat';

      // Start streaming request
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${
              (await supabase.auth.getSession()).data.session?.access_token
            }`,
          },
          body: JSON.stringify({
            conversationId,
            messageId,
            model,
            newMessageId,
          }),
        },
      );

      if (!response.ok) {
        await throwEdgeFunctionError(response, functionName);
      }

      if (response.headers.get('Content-Type')?.includes('application/json')) {
        const data = await response.json();
        if (data.message) {
          return data.message;
        } else {
          throw new Error('No message received');
        }
      }

      async function initialize() {
        // Cancel any pending queries and update conversation leaf ID
        await initializeConversationLeaf(
          queryClient,
          conversationId,
          newMessageId,
        );
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      const decoder = new TextDecoder();
      let leftover = '';

      let finalMessage: Message | null = null;

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          leftover += decoder.decode(value, { stream: true });

          const lines = leftover.split('\n');
          leftover = lines.pop() ?? '';

          for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line) continue;
            try {
              const data: Message = JSON.parse(line);

              finalMessage = data;

              upsertStreamedMessage(queryClient, conversationId, data);

              if (!initialized) {
                await initialize();
                initialized = true;
              }
            } catch (parseError) {
              console.error('Error parsing streaming data:', parseError);
            }
          }
        }

        const flushRemainder = decoder.decode();
        if (flushRemainder) leftover += flushRemainder;
        const tail = leftover.trim();
        if (tail) {
          try {
            const data: Message = JSON.parse(tail);
            finalMessage = data;
            upsertStreamedMessage(queryClient, conversationId, data);
          } catch (parseError) {
            console.error('Error parsing final streaming data:', parseError);
          }
        }
      } finally {
        reader.releaseLock();
      }

      if (!finalMessage) {
        throw new Error('No final message received');
      }

      return finalMessage;
    },
    onSuccess: (newMessage) => {
      messageInsertedConversationUpdate(
        queryClient,
        newMessage,
        conversationId,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['userExtraData'] });
    },
    onError: async (error, { messageId, outputFormat }) => {
      Sentry.captureException(error, {
        extra: {
          hook: 'useParametricChatMutation',
          messageId,
          conversationId,
        },
      });
      // Insert a synthetic assistant message with the structured errorLog
      // so the ErrorLogBubble + Copy-to-Clipboard fire when the parametric
      // mutation fails (edge function unreachable, 5xx, network drop, etc.).
      // Source tag follows the kernel — `implicit-chat` / `parametric-chat` /
      // `mesh-chat` — so support gets a one-glance idea of which path broke.
      try {
        const errMsg = error instanceof Error ? error.message : String(error);
        const errStack = error instanceof Error ? error.stack : undefined;
        const source =
          outputFormat === 'mesh'
            ? 'mesh-chat'
            : outputFormat === 'implicit'
              ? 'implicit-chat'
              : outputFormat === 'unitcell'
                ? 'unitcell-chat'
                : 'parametric-chat';
        await insertMessageAsync({
          role: 'assistant',
          content: {
            text: 'An error occurred while processing your request.',
            errorLog: {
              kind: 'runtime' as const,
              source,
              message: errMsg,
              detail: errStack,
            },
          },
          parent_message_id: messageId,
          conversation_id: conversationId,
        });
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            hook: 'useParametricChatMutation insertMessageAsync',
            messageId,
            conversationId,
          },
        });
      }
    },
  });
}

// Ask-Tom mutation. Drives the side-channel Q&A: user asks a question
// about the current model, the LLM answers in plain prose. NO artifact,
// NO tool calls, NO generation timer. Both the user question and the
// streamed assistant answer get content.kind = 'qa' so the chat renderer
// can group them into the hide/show Q&A track.
//
// Mirrors useParametricChatMutation's fetch + newline-delimited-JSON
// streaming protocol so the wiring on the consumer side stays familiar.
export function useAskTomMutation({
  conversationId,
}: {
  conversationId: string;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();

  return useMutation({
    mutationKey: ['ask-tom', conversationId],
    mutationFn: async ({
      question,
      model,
    }: {
      question: string;
      model: Model;
    }) => {
      // insertMessageAsync supplies the user message id; we only need to
      // pre-allocate the assistant one (the edge function writes to it).
      const assistantMessageId = crypto.randomUUID();

      // 1. Insert the user's question first (kind='qa' flags it for the
      //    chat renderer). Parent is whatever leaf the conversation is at;
      //    we don't move the leaf because Q&A traffic should never be the
      //    parent of a future generation.
      const userMessage = await insertMessageAsync({
        role: 'user',
        content: { text: question, kind: 'qa' },
        conversation_id: conversationId,
        parent_message_id: null,
      });

      // 2. Stream the assistant's answer from the ask-tom edge function.
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask-tom`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${
              (await supabase.auth.getSession()).data.session?.access_token
            }`,
          },
          body: JSON.stringify({
            conversationId,
            messageId: userMessage.id,
            newMessageId: assistantMessageId,
            model,
          }),
        },
      );

      if (!response.ok) {
        const bodyText = await response.text().catch(() => '');
        throw new Error(
          `ask-tom HTTP ${response.status} ${response.statusText}${
            bodyText ? `\n${bodyText.slice(0, 2000)}` : ''
          }`,
        );
      }

      // 3. Read the newline-delimited stream. Each chunk is a Message
      //    snapshot; we cache-set the latest into the conversation so
      //    React Query re-renders the chat as text accumulates.
      const reader = response.body?.getReader();
      if (!reader) throw new Error('ask-tom: no response body');
      const decoder = new TextDecoder();
      let leftover = '';
      let finalMessage: Message | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        leftover += decoder.decode(value, { stream: true });
        const lines = leftover.split('\n');
        leftover = lines.pop() ?? '';
        for (const raw of lines) {
          const line = raw.trim();
          if (!line) continue;
          try {
            const msg: Message = JSON.parse(line);
            finalMessage = msg;
            // Push the streaming snapshot into the React Query cache so
            // the chat re-renders as text grows. The conversation
            // tree-building elsewhere will pick this up.
            queryClient.setQueryData(['message', msg.id], msg);
          } catch (e) {
            console.error('[ask-tom] parse chunk failed', e, line);
          }
        }
      }

      if (!finalMessage) {
        throw new Error('ask-tom: stream ended with no message');
      }
      return finalMessage;
    },
    onSuccess: (newMessage) => {
      messageInsertedConversationUpdate(
        queryClient,
        newMessage,
        conversationId,
      );
    },
    onError: (error) => {
      Sentry.captureException(error, {
        extra: { hook: 'useAskTomMutation', conversationId },
      });
    },
  });
}

export function useUpscaleMutation({
  conversation,
  updateConversationAsync,
}: {
  conversation: Conversation;
  updateConversationAsync?: (conversation: Conversation) => Promise<unknown>;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['upscale', conversation.id],
    mutationFn: async ({
      meshId,
      parentMessageId,
    }: {
      meshId: string;
      parentMessageId: string | null;
    }) => {
      // Immediately navigate to parent message to show loading state
      if (parentMessageId && updateConversationAsync) {
        await updateConversationAsync({
          ...conversation,
          current_message_leaf_id: parentMessageId,
        });
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mesh`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${
              (await supabase.auth.getSession()).data.session?.access_token
            }`,
          },
          body: JSON.stringify({
            action: 'upscale',
            meshId,
            conversationId: conversation.id,
            parentMessageId,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to upscale');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      const decoder = new TextDecoder();
      let leftover = '';
      let finalMessage: Message | null = null;
      let initialized = false;

      async function initialize(messageId: string) {
        await initializeConversationLeaf(
          queryClient,
          conversation.id,
          messageId,
        );
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        leftover += decoder.decode(value, { stream: true });
        const lines = leftover.split('\n');
        leftover = lines.pop() ?? '';

        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line) continue;
          try {
            const data: Message = JSON.parse(line);
            finalMessage = data;

            upsertStreamedMessage(queryClient, conversation.id, data);

            if (!initialized && data.id) {
              await initialize(data.id);
              initialized = true;
            }
          } catch (parseError) {
            console.error('Error parsing streaming data:', parseError);
          }
        }
      }

      // Process remaining data
      const tail = leftover.trim();
      if (tail) {
        try {
          const data: Message = JSON.parse(tail);
          finalMessage = data;
          upsertStreamedMessage(queryClient, conversation.id, data);
        } catch (parseError) {
          console.error('Error parsing final streaming data:', parseError);
        }
      }

      reader.releaseLock();
      return finalMessage;
    },
    onError: (error) => {
      Sentry.captureException(error, {
        extra: {
          hook: 'useUpscaleMutation',
          conversationId: conversation.id,
        },
      });
    },
  });
}
